import React, { useState, useEffect, useRef } from 'react'
import {
  collection,
  query,
  orderBy,
  onSnapshot,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../utils/firebase'
import {
  Mail,
  Plus,
  Pause,
  Play,
  CheckCircle,
  Trash2,
  AlertCircle,
  XCircle,
  Search,
  X,
  Loader,
  MessageCircle,
  Eye,
  Upload,
  Ban,
  FileText,
  Send,
  Download,
} from 'lucide-react'
import './SalesSection.css'

/**
 * Super-admin-only Sales drip dashboard.
 *
 * Lists every prospect in the `salesProspects` collection, with stats
 * across the top, an add-prospect form, and per-row controls to pause,
 * resume, mark-converted, mark-replied, preview emails, or delete.
 * The cron + email sending live in Cloud Functions — this view is just
 * the control surface.
 *
 * A prospect's drip stops automatically when their email shows up in
 * `users` (auto-conversion) or when they click the unsubscribe link in
 * one of the drip emails. Both flows are handled server-side so the
 * status badge here updates without any action from the admin.
 *
 * Additional features:
 *   - CSV bulk import (calls addSalesProspectsBatch)
 *   - Per-prospect Preview modal (calls previewSalesEmail)
 *   - Suppression list view + management
 *   - Stats: sent count, reply rate, conversion rate
 */
function SalesSection({ userProfile }) {
  const [prospects, setProspects] = useState([])
  const [suppressions, setSuppressions] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Add-form state
  const [adding, setAdding] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    neighborhoodName: '',
    companyName: '',
    notes: '',
  })

  // Modals
  const [showImportModal, setShowImportModal] = useState(false)
  const [showSuppressionsModal, setShowSuppressionsModal] = useState(false)
  const [previewProspect, setPreviewProspect] = useState(null)

  // Subscribe to prospects + suppressions in real time.
  useEffect(() => {
    const qProspects = query(
      collection(db, 'salesProspects'),
      orderBy('createdAt', 'desc')
    )
    const unsubP = onSnapshot(
      qProspects,
      (snap) => {
        setProspects(
          snap.docs.map((d) => {
            const data = d.data()
            return {
              id: d.id,
              ...data,
              createdAt: data.createdAt?.toDate?.() || null,
              lastSentAt: data.lastSentAt?.toDate?.() || null,
              nextSendAt: data.nextSendAt?.toDate?.() || null,
            }
          })
        )
        setLoading(false)
      },
      (err) => {
        console.error('Error loading sales prospects:', err)
        setError(err.message)
        setLoading(false)
      }
    )

    const qSupp = query(collection(db, 'suppressedEmails'))
    const unsubS = onSnapshot(
      qSupp,
      (snap) => {
        setSuppressions(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            addedAt: d.data().addedAt?.toDate?.() || null,
          }))
        )
      },
      (err) => {
        // Non-fatal: suppressions panel just stays empty.
        console.error('Error loading suppressions:', err)
      }
    )

    return () => {
      unsubP()
      unsubS()
    }
  }, [])

  // Auto-clear success banner after a few seconds.
  useEffect(() => {
    if (!success) return
    const t = setTimeout(() => setSuccess(''), 4000)
    return () => clearTimeout(t)
  }, [success])

  // ============ Stats ============
  const stats = {
    active: prospects.filter((p) => p.status === 'active').length,
    paused: prospects.filter((p) => p.status === 'paused').length,
    replied: prospects.filter((p) => p.status === 'replied').length,
    converted: prospects.filter((p) => p.status === 'converted').length,
    unsubscribed: prospects.filter((p) => p.status === 'unsubscribed').length,
    completed: prospects.filter((p) => p.status === 'completed').length,
    total: prospects.length,
  }

  // Performance metrics: anyone with sequenceStep >= 1 has received
  // at least Email #1. Reply rate = replied / sent. Conversion = converted / sent.
  const sentCount = prospects.filter((p) => (p.sequenceStep || 0) >= 1).length
  const replyRate = sentCount > 0
    ? Math.round((stats.replied / sentCount) * 1000) / 10
    : 0
  const conversionRate = sentCount > 0
    ? Math.round((stats.converted / sentCount) * 1000) / 10
    : 0

  // ============ Filtered list ============
  const filteredProspects = prospects
    .filter((p) => {
      if (statusFilter === 'all') return true
      return p.status === statusFilter
    })
    .filter((p) => {
      if (!searchQuery.trim()) return true
      const q = searchQuery.trim().toLowerCase()
      return (
        (p.email || '').toLowerCase().includes(q) ||
        (p.firstName || '').toLowerCase().includes(q) ||
        (p.neighborhoodName || '').toLowerCase().includes(q) ||
        (p.companyName || '').toLowerCase().includes(q)
      )
    })

  // ============ Add prospect ============
  const handleAddProspect = async (e) => {
    e.preventDefault()
    setError('')
    if (!formData.firstName.trim() || !formData.email.trim()) {
      setError('First name and email are required.')
      return
    }
    if (!formData.neighborhoodName.trim()) {
      setError('Neighborhood name is required — the emails are personalized around it.')
      return
    }
    setAdding(true)
    try {
      const addFn = httpsCallable(functions, 'addSalesProspect')
      await addFn({
        firstName: formData.firstName.trim(),
        email: formData.email.trim().toLowerCase(),
        neighborhoodName: formData.neighborhoodName.trim() || null,
        companyName: formData.companyName.trim() || null,
        notes: formData.notes.trim() || null,
      })
      setSuccess(`${formData.firstName.trim()} added — first email queued for next Mon-Fri 9 AM CT.`)
      setFormData({
        firstName: '',
        email: '',
        neighborhoodName: '',
        companyName: '',
        notes: '',
      })
      setShowAddForm(false)
    } catch (err) {
      setError(err?.message || 'Failed to add prospect.')
    } finally {
      setAdding(false)
    }
  }

  // ============ Per-row actions ============
  const callStatusUpdate = async (prospect, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return
    setError('')
    try {
      const fn = httpsCallable(functions, 'updateSalesProspectStatus')
      await fn({ prospectId: prospect.id, action })
    } catch (err) {
      setError(err?.message || `Failed to ${action} prospect.`)
    }
  }

  const handlePause = (p) => callStatusUpdate(p, 'pause')
  const handleResume = (p) => callStatusUpdate(p, 'resume')
  const handleMarkConverted = (p) =>
    callStatusUpdate(
      p,
      'markConverted',
      `Mark ${p.firstName || p.email} as converted? They'll stop receiving drip emails.`
    )
  const handleMarkReplied = (p) =>
    callStatusUpdate(
      p,
      'markReplied',
      `Mark ${p.firstName || p.email} as replied? They'll stop receiving follow-up emails.`
    )
  const handleDelete = (p) =>
    callStatusUpdate(
      p,
      'delete',
      `Delete ${p.firstName || p.email}? This cannot be undone.`
    )

  // ============ Date formatting ============
  const fmtDate = (d) => {
    if (!d) return '—'
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }
  const fmtRelative = (d) => {
    if (!d) return '—'
    const diffMs = d.getTime() - Date.now()
    const absMs = Math.abs(diffMs)
    const minutes = Math.round(absMs / 60000)
    const hours = Math.round(absMs / 3600000)
    const days = Math.round(absMs / 86400000)
    const past = diffMs < 0
    if (absMs < 60 * 1000) return past ? 'just now' : 'in <1 min'
    if (minutes < 60) return past ? `${minutes}m ago` : `in ${minutes}m`
    if (hours < 24) return past ? `${hours}h ago` : `in ${hours}h`
    return past ? `${days}d ago` : `in ${days}d`
  }

  // ============ Drip phase label ============
  // The drip has three phases: first 2 weeks (4 emails), then ongoing
  // monthly nurture. The label is shown next to "Email N" in each
  // prospect row so the admin can see at a glance where in the funnel
  // each prospect sits.
  const dripPhaseLabel = (sequenceStep) => {
    if (sequenceStep <= 1) return 'Week 1'
    if (sequenceStep <= 3) return 'Week 2'
    return 'Monthly nurture'
  }

  // ============ Status badge ============
  const statusBadge = (status) => {
    const map = {
      active: { label: 'Active', cls: 'sales-badge-active' },
      paused: { label: 'Paused', cls: 'sales-badge-paused' },
      replied: { label: 'Replied', cls: 'sales-badge-replied' },
      converted: { label: 'Converted', cls: 'sales-badge-converted' },
      unsubscribed: { label: 'Unsubscribed', cls: 'sales-badge-unsubscribed' },
      completed: { label: 'Sequence done', cls: 'sales-badge-completed' },
    }
    const cfg = map[status] || { label: status, cls: 'sales-badge-active' }
    return <span className={`sales-badge ${cfg.cls}`}>{cfg.label}</span>
  }

  return (
    <div className="sales-section">
      <div className="sales-header">
        <h2>Sales Drip</h2>
        <div className="sales-header-actions">
          <button
            className="btn-outline"
            onClick={() => setShowSuppressionsModal(true)}
            title="View suppression list"
          >
            <Ban size={16} /> Suppressions ({suppressions.length})
          </button>
          <button
            className="btn-outline"
            onClick={() => setShowImportModal(true)}
          >
            <Upload size={16} /> Bulk Import
          </button>
          <button
            className="btn-primary"
            onClick={() => setShowAddForm((v) => !v)}
          >
            {showAddForm ? (
              <>
                <X size={16} /> Cancel
              </>
            ) : (
              <>
                <Plus size={16} /> Add Prospect
              </>
            )}
          </button>
        </div>
      </div>

      <p className="sales-tagline">
        Send a three-email cold-outreach sequence to potential customers. Drip
        stops automatically on conversion, reply, or unsubscribe.
      </p>

      {error && (
        <div className="sales-banner sales-banner-error">
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError('')} className="sales-banner-close">
            <X size={14} />
          </button>
        </div>
      )}
      {success && (
        <div className="sales-banner sales-banner-success">
          <CheckCircle size={16} /> {success}
          <button onClick={() => setSuccess('')} className="sales-banner-close">
            <X size={14} />
          </button>
        </div>
      )}

      {/* ============ Pipeline status row ============ */}
      <div className="sales-stats">
        <div className="sales-stat">
          <span className="sales-stat-value">{stats.active}</span>
          <span className="sales-stat-label">Active</span>
        </div>
        <div className="sales-stat">
          <span className="sales-stat-value">{stats.paused}</span>
          <span className="sales-stat-label">Paused</span>
        </div>
        <div className="sales-stat sales-stat-replied">
          <span className="sales-stat-value">{stats.replied}</span>
          <span className="sales-stat-label">Replied</span>
        </div>
        <div className="sales-stat sales-stat-converted">
          <span className="sales-stat-value">{stats.converted}</span>
          <span className="sales-stat-label">Converted</span>
        </div>
        <div className="sales-stat">
          <span className="sales-stat-value">{stats.unsubscribed}</span>
          <span className="sales-stat-label">Unsubscribed</span>
        </div>
        <div className="sales-stat">
          <span className="sales-stat-value">{stats.completed}</span>
          <span className="sales-stat-label">Done</span>
        </div>
      </div>

      {/* ============ Performance row ============ */}
      <div className="sales-stats sales-stats-secondary">
        <div className="sales-stat">
          <span className="sales-stat-value">{sentCount}</span>
          <span className="sales-stat-label">Emails sent</span>
        </div>
        <div className="sales-stat">
          <span className="sales-stat-value">{replyRate}%</span>
          <span className="sales-stat-label">Reply rate</span>
        </div>
        <div className="sales-stat">
          <span className="sales-stat-value">{conversionRate}%</span>
          <span className="sales-stat-label">Conversion rate</span>
        </div>
        <div className="sales-stat">
          <span className="sales-stat-value">{stats.total}</span>
          <span className="sales-stat-label">Total prospects</span>
        </div>
      </div>

      {/* ============ Add form ============ */}
      {showAddForm && (
        <form className="sales-add-form" onSubmit={handleAddProspect}>
          <div className="sales-form-row">
            <label>
              First Name *
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                placeholder="Sarah"
                disabled={adding}
              />
            </label>
            <label>
              Email *
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="sarah@example.com"
                disabled={adding}
              />
            </label>
          </div>
          <div className="sales-form-row">
            <label>
              Neighborhood *
              <input
                type="text"
                value={formData.neighborhoodName}
                onChange={(e) =>
                  setFormData({ ...formData, neighborhoodName: e.target.value })
                }
                placeholder="Menlo Park"
                disabled={adding}
              />
            </label>
            <label>
              Company
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) =>
                  setFormData({ ...formData, companyName: e.target.value })
                }
                placeholder="(Optional)"
                disabled={adding}
              />
            </label>
          </div>
          <label>
            Notes (for your reference only)
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Where you found them, what you discussed, etc."
              rows={2}
              disabled={adding}
            />
          </label>
          <div className="sales-form-actions">
            <button type="submit" className="btn-primary" disabled={adding}>
              {adding ? (
                <>
                  <Loader size={16} className="spin" /> Adding…
                </>
              ) : (
                <>
                  <Mail size={16} /> Add &amp; Start Drip
                </>
              )}
            </button>
          </div>
          <p className="sales-form-hint">
            Emails fire Mon–Fri 9 AM–noon CT. Email{' '}
            <strong>1</strong> goes out at the next valid window, email{' '}
            <strong>2</strong> 4 days later, email <strong>3</strong> on day 9.
          </p>
        </form>
      )}

      {/* ============ Filter + search ============ */}
      <div className="sales-filters">
        <div className="sales-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search by name, email, or neighborhood…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="sales-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="sales-status-filter">
          {[
            { id: 'all', label: `All (${stats.total})` },
            { id: 'active', label: `Active (${stats.active})` },
            { id: 'paused', label: `Paused (${stats.paused})` },
            { id: 'replied', label: `Replied (${stats.replied})` },
            { id: 'converted', label: `Converted (${stats.converted})` },
            { id: 'unsubscribed', label: `Unsub (${stats.unsubscribed})` },
            { id: 'completed', label: `Done (${stats.completed})` },
          ].map((f) => (
            <button
              key={f.id}
              className={`sales-filter-chip ${
                statusFilter === f.id ? 'active' : ''
              }`}
              onClick={() => setStatusFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ============ Prospect list ============ */}
      {loading ? (
        <div className="sales-loading">
          <Loader size={20} className="spin" /> Loading prospects…
        </div>
      ) : filteredProspects.length === 0 ? (
        <div className="sales-empty">
          <Mail size={36} />
          <p>
            {prospects.length === 0
              ? 'No prospects yet. Click "Add Prospect" to start a drip.'
              : 'No prospects match the current filter.'}
          </p>
        </div>
      ) : (
        <div className="sales-list">
          {filteredProspects.map((p) => (
            <div key={p.id} className="sales-row">
              <div className="sales-row-main">
                <div className="sales-row-name">
                  <strong>{p.firstName || '(no name)'}</strong>
                  {statusBadge(p.status)}
                </div>
                <div className="sales-row-email">{p.email}</div>
                {(p.neighborhoodName || p.companyName) && (
                  <div className="sales-row-meta">
                    {p.neighborhoodName && (
                      <span>{p.neighborhoodName}</span>
                    )}
                    {p.neighborhoodName && p.companyName && <span> · </span>}
                    {p.companyName && <span>{p.companyName}</span>}
                  </div>
                )}
                {p.notes && (
                  <div className="sales-row-notes">{p.notes}</div>
                )}
              </div>

              <div className="sales-row-progress">
                <div className="sales-progress-step">
                  Email {(p.sequenceStep || 0) + 1} · {dripPhaseLabel(p.sequenceStep || 0)}
                </div>
                <div className="sales-progress-dates">
                  <div>Last: {fmtDate(p.lastSentAt)}</div>
                  {p.status === 'active' && p.nextSendAt && (
                    <div>Next: {fmtRelative(p.nextSendAt)}</div>
                  )}
                </div>
              </div>

              <div className="sales-row-actions">
                <button
                  className="btn-outline small"
                  onClick={() => setPreviewProspect(p)}
                  title="Preview emails this prospect will receive"
                >
                  <Eye size={14} /> Preview
                </button>
                {p.status === 'active' && (
                  <>
                    <button
                      className="btn-outline small"
                      onClick={() => handleMarkReplied(p)}
                      title="Mark as replied (stops follow-ups)"
                    >
                      <MessageCircle size={14} /> Replied
                    </button>
                    <button
                      className="btn-outline small"
                      onClick={() => handlePause(p)}
                      title="Pause drip"
                    >
                      <Pause size={14} /> Pause
                    </button>
                  </>
                )}
                {p.status === 'paused' && (
                  <button
                    className="btn-outline small"
                    onClick={() => handleResume(p)}
                    title="Resume drip"
                  >
                    <Play size={14} /> Resume
                  </button>
                )}
                {(p.status === 'active' || p.status === 'paused' || p.status === 'replied') && (
                  <button
                    className="btn-outline small"
                    onClick={() => handleMarkConverted(p)}
                    title="Mark as converted (stops drip)"
                  >
                    <CheckCircle size={14} /> Converted
                  </button>
                )}
                <button
                  className="btn-outline small danger"
                  onClick={() => handleDelete(p)}
                  title="Delete prospect"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ============ Bulk import modal ============ */}
      {showImportModal && (
        <BulkImportModal
          onClose={() => setShowImportModal(false)}
          onSuccess={(msg) => {
            setSuccess(msg)
            setShowImportModal(false)
          }}
        />
      )}

      {/* ============ Suppressions modal ============ */}
      {showSuppressionsModal && (
        <SuppressionsModal
          suppressions={suppressions}
          onClose={() => setShowSuppressionsModal(false)}
          onError={(msg) => setError(msg)}
          onSuccess={(msg) => setSuccess(msg)}
        />
      )}

      {/* ============ Preview modal ============ */}
      {previewProspect && (
        <PreviewModal
          prospect={previewProspect}
          onClose={() => setPreviewProspect(null)}
        />
      )}
    </div>
  )
}

// ============================================================
// BULK IMPORT MODAL
// ============================================================

/**
 * Minimal RFC 4180-ish CSV parser. Handles quoted fields with commas,
 * escaped double quotes (""), and CRLF/LF line endings. Sufficient for
 * prospect lists exported from Sheets/Excel.
 */
function parseCSV(text) {
  const rows = []
  let row = []
  let field = ''
  let inQuotes = false
  let i = 0
  while (i < text.length) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') {
        field += '"'
        i += 2
        continue
      }
      if (c === '"') {
        inQuotes = false
        i++
        continue
      }
      field += c
      i++
    } else {
      if (c === '"') {
        inQuotes = true
        i++
        continue
      }
      if (c === ',') {
        row.push(field)
        field = ''
        i++
        continue
      }
      if (c === '\r') {
        i++
        continue
      }
      if (c === '\n') {
        row.push(field)
        rows.push(row)
        row = []
        field = ''
        i++
        continue
      }
      field += c
      i++
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field)
    rows.push(row)
  }
  return rows
}

function BulkImportModal({ onClose, onSuccess }) {
  const [csvText, setCsvText] = useState('')
  const [parsedRows, setParsedRows] = useState([])
  const [importing, setImporting] = useState(false)
  const [results, setResults] = useState(null)
  const [error, setError] = useState('')
  const fileInputRef = useRef(null)

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = (ev.target && ev.target.result) || ''
      setCsvText(text)
      parseAndPreview(text)
    }
    reader.readAsText(file)
  }

  /**
   * Generate a sample CSV with the right header row and a few example
   * prospects, then trigger a browser download. Headers carry
   * "(required)" / "(optional)" hints so the file is self-documenting
   * when opened in Excel/Sheets; the parser strips parens before
   * matching so the annotations don't break import.
   */
  const handleDownloadTemplate = () => {
    const csv = [
      'firstName (required),email (required),neighborhoodName (required),companyName (optional),notes (optional)',
      'Sarah,sarah@example.com,Riverbend HOA,Premier Management,Met at CAI conference',
      'Tom,tom@example.com,Oakwood Estates,,Found via LinkedIn',
      'Maria,maria@example.com,Hilltop Community,Hilltop HOA Inc,',
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'neighbortopia-prospect-template.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const parseAndPreview = (text) => {
    setError('')
    const raw = parseCSV(text)
    if (raw.length === 0) {
      setParsedRows([])
      return
    }
    // Normalize a header cell: lowercase, strip anything in parentheses
    // (so "companyName (optional)" matches "companyname"), then strip
    // any whitespace/underscores/dashes. Tolerant of Excel/Sheets users
    // who add their own column-name conventions.
    const normalize = (h) =>
      (h || '')
        .trim()
        .toLowerCase()
        .replace(/\(.*?\)/g, '')
        .replace(/[\s_-]/g, '')
    const headerRow = raw[0].map(normalize)
    const required = ['firstname', 'email']
    const hasHeader = required.every((r) => headerRow.includes(r))
    const dataRows = hasHeader ? raw.slice(1) : raw
    const headerMap = hasHeader
      ? headerRow
      : ['firstname', 'email', 'neighborhoodname', 'companyname', 'notes']

    const rows = dataRows
      .filter((r) => r.some((cell) => (cell || '').trim()))
      .map((cells) => {
        const obj = {}
        headerMap.forEach((key, idx) => {
          if (cells[idx] !== undefined) {
            obj[key] = (cells[idx] || '').trim()
          }
        })
        return {
          firstName: obj.firstname || '',
          email: (obj.email || '').toLowerCase(),
          neighborhoodName: obj.neighborhoodname || '',
          companyName: obj.companyname || '',
          notes: obj.notes || '',
        }
      })
    setParsedRows(rows)
  }

  const handleImport = async () => {
    setImporting(true)
    setError('')
    setResults(null)
    try {
      const fn = httpsCallable(functions, 'addSalesProspectsBatch')
      const res = await fn({ rows: parsedRows })
      const data = res.data || {}
      setResults(data)
      if (data.createdCount > 0) {
        onSuccess(
          `Imported ${data.createdCount} prospect${data.createdCount === 1 ? '' : 's'} (skipped ${data.totalRows - data.createdCount}).`
        )
      } else {
        setError('No prospects imported — see results below.')
      }
    } catch (err) {
      setError(err?.message || 'Import failed.')
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="sales-modal-backdrop" onClick={onClose}>
      <div className="sales-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sales-modal-header">
          <h3>
            <Upload size={18} /> Bulk Import Prospects
          </h3>
          <button className="sales-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="sales-modal-body">
          <p className="sales-modal-hint">
            Upload a CSV. Required columns:{' '}
            <code>firstName</code>, <code>email</code>,{' '}
            <code>neighborhoodName</code>. Optional columns:{' '}
            <code>companyName</code>, <code>notes</code>. First row should
            be a header. Max 500 rows per import.
          </p>

          <div className="sales-import-controls">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              style={{ display: 'none' }}
            />
            <button
              className="btn-outline"
              onClick={handleDownloadTemplate}
              disabled={importing}
              title="Download a sample CSV with the right column headers"
            >
              <Download size={16} /> Download Template
            </button>
            <button
              className="btn-outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              <FileText size={16} /> Choose CSV file
            </button>
            <span className="sales-import-or">or paste CSV text below</span>
          </div>

          <textarea
            className="sales-csv-input"
            placeholder={'firstName,email,neighborhoodName,companyName\nSarah,sarah@example.com,Riverbend HOA,Premier Mgmt\nTom,tom@example.com,Oakwood Estates,'}
            value={csvText}
            onChange={(e) => {
              setCsvText(e.target.value)
              parseAndPreview(e.target.value)
            }}
            rows={6}
            disabled={importing}
          />

          {parsedRows.length > 0 && (
            <div className="sales-import-preview">
              <div className="sales-import-preview-header">
                <strong>{parsedRows.length}</strong> row
                {parsedRows.length === 1 ? '' : 's'} parsed
              </div>
              <table className="sales-import-table">
                <thead>
                  <tr>
                    <th>First Name</th>
                    <th>Email</th>
                    <th>Neighborhood</th>
                    <th>Company</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.slice(0, 8).map((r, i) => (
                    <tr key={i}>
                      <td>{r.firstName || <em>—</em>}</td>
                      <td>{r.email || <em>—</em>}</td>
                      <td>{r.neighborhoodName || <em>—</em>}</td>
                      <td>{r.companyName || <em>—</em>}</td>
                    </tr>
                  ))}
                  {parsedRows.length > 8 && (
                    <tr>
                      <td colSpan={4} className="sales-import-more">
                        …and {parsedRows.length - 8} more
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {error && (
            <div className="sales-banner sales-banner-error">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {results && (
            <div className="sales-import-results">
              <div>
                <strong>Created:</strong> {results.createdCount} ·{' '}
                <strong>Skipped:</strong>{' '}
                {results.totalRows - results.createdCount}
              </div>
              {results.results && results.results.some((r) => r.status === 'skipped') && (
                <details className="sales-import-skipped">
                  <summary>
                    Show skipped rows (
                    {results.results.filter((r) => r.status === 'skipped').length})
                  </summary>
                  <ul>
                    {results.results
                      .filter((r) => r.status === 'skipped')
                      .map((r, i) => (
                        <li key={i}>
                          Row {r.row}{' '}
                          {r.email && <code>({r.email})</code>}: {r.reason}
                        </li>
                      ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="sales-modal-footer">
          <button className="btn-outline" onClick={onClose} disabled={importing}>
            {results ? 'Done' : 'Cancel'}
          </button>
          {!results && (
            <button
              className="btn-primary"
              onClick={handleImport}
              disabled={importing || parsedRows.length === 0}
            >
              {importing ? (
                <>
                  <Loader size={16} className="spin" /> Importing…
                </>
              ) : (
                <>
                  <Send size={16} /> Import {parsedRows.length} prospect
                  {parsedRows.length === 1 ? '' : 's'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================
// SUPPRESSIONS MODAL
// ============================================================
function SuppressionsModal({ suppressions, onClose, onError, onSuccess }) {
  const [addEmail, setAddEmail] = useState('')
  const [busy, setBusy] = useState(false)

  const callSuppression = async (email, action) => {
    setBusy(true)
    try {
      const fn = httpsCallable(functions, 'manageSalesSuppression')
      await fn({ email, action })
      if (action === 'add') {
        onSuccess(`${email} added to suppression list.`)
        setAddEmail('')
      } else {
        onSuccess(`${email} removed from suppression list.`)
      }
    } catch (err) {
      onError(err?.message || 'Suppression action failed.')
    } finally {
      setBusy(false)
    }
  }

  const handleAdd = (e) => {
    e.preventDefault()
    if (!addEmail.trim()) return
    callSuppression(addEmail.trim().toLowerCase(), 'add')
  }

  const handleRemove = (email) => {
    if (!window.confirm(`Remove ${email} from suppression list? They could be re-prospected.`)) return
    callSuppression(email, 'remove')
  }

  return (
    <div className="sales-modal-backdrop" onClick={onClose}>
      <div className="sales-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sales-modal-header">
          <h3>
            <Ban size={18} /> Suppression List
          </h3>
          <button className="sales-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="sales-modal-body">
          <p className="sales-modal-hint">
            Emails on this list will never receive drip outreach. Addresses
            are added automatically when someone unsubscribes. You can also
            add an address here manually (e.g., a hard bounce or an explicit
            opt-out request).
          </p>

          <form className="sales-supp-add" onSubmit={handleAdd}>
            <input
              type="email"
              placeholder="someone@example.com"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              disabled={busy}
            />
            <button
              type="submit"
              className="btn-primary"
              disabled={busy || !addEmail.trim()}
            >
              <Plus size={14} /> Suppress
            </button>
          </form>

          {suppressions.length === 0 ? (
            <div className="sales-empty">
              <Ban size={36} />
              <p>No suppressed emails yet.</p>
            </div>
          ) : (
            <ul className="sales-supp-list">
              {suppressions
                .slice()
                .sort((a, b) => {
                  const ta = a.addedAt?.getTime?.() || 0
                  const tb = b.addedAt?.getTime?.() || 0
                  return tb - ta
                })
                .map((s) => (
                  <li key={s.id} className="sales-supp-row">
                    <div>
                      <div className="sales-supp-email">{s.email}</div>
                      <div className="sales-supp-meta">
                        {s.reason || 'unsubscribed'}
                        {s.addedAt &&
                          ` · ${s.addedAt.toLocaleDateString()}`}
                      </div>
                    </div>
                    <button
                      className="btn-outline small danger"
                      onClick={() => handleRemove(s.email)}
                      disabled={busy}
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </div>

        <div className="sales-modal-footer">
          <button className="btn-outline" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// EMAIL PREVIEW MODAL
// ============================================================
function PreviewModal({ prospect, onClose }) {
  const [stepIndex, setStepIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    const fn = httpsCallable(functions, 'previewSalesEmail')
    fn({ prospectId: prospect.id, stepIndex })
      .then((res) => {
        if (cancelled) return
        setPreview(res.data || null)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err?.message || 'Failed to load preview.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [stepIndex, prospect.id])

  return (
    <div className="sales-modal-backdrop" onClick={onClose}>
      <div className="sales-modal sales-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="sales-modal-header">
          <h3>
            <Eye size={18} /> Email Preview — {prospect.firstName || prospect.email}
          </h3>
          <button className="sales-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="sales-preview-tabs">
          {[
            { idx: 0, label: 'Day 0' },
            { idx: 1, label: 'Day 3' },
            { idx: 2, label: 'Day 6' },
            { idx: 3, label: 'Day 13' },
            { idx: 4, label: 'Monthly' },
          ].map(({ idx, label }) => (
            <button
              key={idx}
              className={`sales-preview-tab ${stepIndex === idx ? 'active' : ''}`}
              onClick={() => setStepIndex(idx)}
            >
              Email {idx + 1}{' '}
              <span className="sales-preview-tab-day">({label})</span>
            </button>
          ))}
        </div>

        <div className="sales-modal-body">
          {error && (
            <div className="sales-banner sales-banner-error">
              <AlertCircle size={16} /> {error}
            </div>
          )}
          {loading || !preview ? (
            <div className="sales-loading">
              <Loader size={20} className="spin" /> Loading preview…
            </div>
          ) : (
            <>
              <div className="sales-preview-meta">
                <div>
                  <span className="sales-preview-label">From:</span>{' '}
                  {preview.from}
                </div>
                <div>
                  <span className="sales-preview-label">Reply-To:</span>{' '}
                  {preview.replyTo}
                </div>
                <div>
                  <span className="sales-preview-label">To:</span>{' '}
                  {prospect.email}
                </div>
                <div>
                  <span className="sales-preview-label">Subject:</span>{' '}
                  <strong>{preview.subject}</strong>
                </div>
              </div>
              <div className="sales-preview-html-wrap">
                <iframe
                  className="sales-preview-html"
                  title="Email preview"
                  sandbox=""
                  srcDoc={preview.html}
                />
              </div>
            </>
          )}
        </div>

        <div className="sales-modal-footer">
          <button className="btn-outline" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default SalesSection
